<template>
  <a-modal
    v-model:visible="visible"
    :title="t('workspaceSettings.identifiers.editModal.title')"
    :on-before-ok="handleBeforeOk"
    @cancel="handleCancel"
  >
    <a-form ref="formRef" :model="form" layout="vertical">
      <a-form-item
        field="name"
        :label="t('workspaceSettings.identifiers.createModal.name')"
      >
        <div class="name-container">
          <a-input v-model="form.name" disabled />
          <a-tooltip
            :content="t('workspaceSettings.identifiers.editModal.nameTooltip')"
          >
            <icon-info-circle class="info-icon" />
          </a-tooltip>
        </div>
      </a-form-item>
      <a-form-item
        field="description"
        :label="t('workspaceSettings.identifiers.createModal.desc')"
      >
        <a-input
          v-model="form.description"
          :placeholder="
            t('workspaceSettings.identifiers.createModal.descPlaceholder')
          "
          allow-clear
        />
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<script setup lang="ts">
  import { ref, reactive, computed, watch } from 'vue';
  import Message from '@/utils/message';
  import { IconInfoCircle } from '@arco-design/web-vue/es/icon';
  import { useI18n } from 'vue-i18n';
  import { updateProtocolMetaApi } from '@/api/protocol';
  import type { ProtocolItem } from '@/types/protocol';
  import { useUserStore } from '@/store';

  const props = defineProps<{
    data: ProtocolItem | null;
  }>();

  const emit = defineEmits<{
    (e: 'success'): void;
  }>();

  const visible = defineModel('visible', { type: Boolean, default: false });

  const { t } = useI18n();
  const userStore = useUserStore();
  const formRef = ref();
  const form = reactive({
    name: '',
    description: '',
  });

  watch(
    () => props.data,
    (val) => {
      if (val) {
        form.name = val.name;
        form.description = val.description;
      }
    },
    { immediate: true }
  );

  const handleCancel = () => {
    visible.value = false;
    formRef.value?.resetFields();
  };

  const handleBeforeOk = async () => {
    try {
      if (!props.data) return false;
      await updateProtocolMetaApi({
        name: form.name,
        description: form.description,
        tenant_uuid: userStore.tenantUuid!,
        workspace_uuid: userStore.workspaceUuid!,
      });
      Message.success(
        t('workspaceSettings.identifiers.message.updatedSuccess')
      );
      emit('success');
      return true;
    } catch (error) {
      return false;
    }
  };
</script>

<style lang="less" scoped>
  .name-container {
    position: relative;
    width: 100%;

    .info-icon {
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: var(--color-text-3);
      cursor: help;
    }
  }
</style>
